import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Box } from '@/components/ui/lib/box';
import { Card } from '@/components/ui/lib/card';
import { Container } from '@/components/ui/lib/container';
import { Heading } from '@/components/ui/lib/heading';
import { Text } from '@/components/ui/lib/text';
import RouteError from '@/components/ui/route-error';
import SignInFlow from '@/components/ui/sign-in-flow';
import { DICTIONARY } from '@/config/dictionary';

/**
 * Where the emailed sign-in link lands, and a direct entry point for anyone who bookmarks it. The
 * flow itself is the shared `SignInFlow` — the landing page's sign-in tab renders the same
 * component, so there is one implementation of sign-in, not two.
 *
 * No `loader`: nothing here is cached server state. The token in the query string is peeked once
 * on mount by `SignInFlow`, and prefetching that peek would put a credential lookup in the router
 * rather than behind the deliberate click that spends it.
 */
const SignInPage = () => {
  const { token } = Route.useSearch();
  const d = DICTIONARY.auth.signInFlow;

  return (
    <Box as="section" minH="calc(100vh - 72px)" className="bg-subtle">
      <Container maxW="lg" px={{ base: '6', sm: '8' }} py="16">
        <Box textAlign="center" mb="8">
          <Text
            fontSize="xs"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="wider"
            className="text-accent"
            mb="3"
          >
            {DICTIONARY.auth.signIn.idle}
          </Text>
          <Heading as="h1" fontSize="3xl" fontWeight="800" className="text-base" mb="2">
            {d.pageTitle}
          </Heading>
          <Text fontSize="sm" className="text-muted">
            {d.subtitle}
          </Text>
        </Box>

        <Card.Root className="bg-page border-rim">
          <Card.Body p="8">
            <SignInFlow token={token} />
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
};

export const Route = createFileRoute('/signin')({
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  component: SignInPage,
  errorComponent: RouteError,
});
